<template>
  <div class="docs-tree-item">
    <div class="docs-tree-link-wrapper" :class="dense ? 'py-1' : 'py-1.5'" :style="{ paddingLeft: `${indentBase + depth * indentStep}rem` }">
      <router-link :to="pageRoute" :class="['docs-tree-link', dense ? 'text-sm' : 'text-base', { 'docs-tree-link--active': isActive }]">
        <component :is="hasChildren ? (isExpanded ? FolderOpen : Folder) : FileText" :size="16" />
        <span>{{ page.title }}</span>
      </router-link>

      <button v-if="hasChildren" type="button" class="docs-tree-toggle" :class="{ 'docs-tree-toggle--expanded': isExpanded }" @click="toggleExpanded">
        <ChevronRight :size="14" />
      </button>
    </div>

    <div v-if="hasChildren && children.length > 0 && isExpanded" class="docs-tree-children">
      <DocsTreeNode v-for="child in children" :key="child.id" :page="child" :current-slug="currentSlug" :page-type="pageType" :depth="depth + 1" :dense="dense" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Folder, FolderOpen, FileText, ChevronRight } from 'lucide-vue-next';

const props = defineProps({
  page: { type: Object, required: true },
  currentSlug: { type: String, required: true },
  pageType: { type: String, required: true },
  depth: { type: Number, default: 0 },
  dense: { type: Boolean, default: false },
});

const children = computed(() => props.page.children || []);
const hasChildren = computed(() => children.value.length > 0);
const isExpanded = ref(false);
const indentBase = props.dense ? 0.5 : 0.75;
const indentStep = props.dense ? 0.5 : 0.75;

// Fix: Active logic for parent-child relationship
const isActive = computed(() => {
  const currentPath = props.currentSlug;
  const pagePath = props.page.path || props.page.slug;

  // Exact match
  if (currentPath === pagePath) return true;

  // Child is active (current path starts with page path)
  if (currentPath && pagePath && currentPath.startsWith(pagePath + '/')) return true;

  return false;
});

const pageRoute = computed(() => {
  const slug = props.page.path || props.page.slug;
  return {
    name: props.pageType,
    params: { slug: slug },
  };
});

watch(
  () => props.currentSlug,
  newSlug => {
    if (newSlug && props.page.path) {
      if (newSlug.startsWith(props.page.path + '/')) {
        isExpanded.value = true;
      }
    }
  },
  { immediate: true }
);

watch(
  () => isActive.value,
  active => {
    if (active) {
      isExpanded.value = true;
    }
  },
  { immediate: true }
);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>
